/**
 * OWA-525: the notice shown when a timer stop adds no time. A stop adds nothing when its delta
 * is negative (this device's clock is behind the one that started the timer) or 24 hours or more
 * (a forgotten timer, or a clock that differs by that much). The user is asked to enter the time
 * actually worked, and the notice offers to open the task for that.
 *
 * The notice is a toast in the app's ToastContainer (App.tsx), the same place AlertToasts shows
 * the store's alerts. It stays until the user dismisses it.
 *
 * @module client/TaskManagement/timerCapNotice
 */
import {FC, useCallback} from 'react';
import {Button} from '@mui/material';
import {toast} from 'react-toastify';
import {useStoreValue} from 'zustand-x';

import type Task from '@/entities/task';
import store from '@/store/store';
import {dialogStore} from '@/store/zstore';
import {DialogType} from '@/store/dialogs';
import './timerCapNotice.less';

export const TIMER_CAP_NOTICE = 'The timer ran for 24 hours or more, or this device\'s clock differs from the one that started it. No time was added. Enter the time you worked.';
export const ENTER_TIME = 'Enter Time';

/** The toast id: one notice per task, so a second stop of the same task does not stack another. */
export const timerCapNoticeId = (taskId: string): string => `timer-cap-${taskId}`;

/**
 * The task to open: the store's copy once it holds the stopped version, otherwise the stopped
 * copy the notice was raised with (the save may not have reached the store yet).
 */
function taskToOpen(stopped: Task): Task {
    const live: Task | undefined = store.getState()?.tasks?.get?.(stopped.id);
    return live && !live.running && live.status !== 'D' ? live : stopped;
}

type Props = {
    task: Task,
    closeToast?: () => void,
};

export const TimerCapNotice: FC<Props> = ({task, closeToast}) => {
    const dialogOpen = useStoreValue(dialogStore, 'isOpen');
    const dialogType = useStoreValue(dialogStore, 'dialogType');
    // The app has one dialog: opening the task would replace an editor that is already open
    const editorOpen = dialogOpen && dialogType === DialogType.Task;

    const onEnterTime = useCallback(async () => {
        closeToast?.();
        const {editTask} = await import('./TaskHelper');
        editTask(taskToOpen(task));
    }, [closeToast, task]);

    return (
        <div className='timer-cap-notice'>
            <p><strong>{task.title || 'This task'}</strong></p>
            <p>{TIMER_CAP_NOTICE}</p>
            {!editorOpen && <div className='timer-cap-notice__actions'>
                <Button size='small' variant='text' onClick={onEnterTime}>{ENTER_TIME}</Button>
            </div>}
        </div>
    );
};

/**
 * Show the notice for a stop that added no time.
 *
 * @param {Task} task - the stopped task
 */
export function showTimerCapNotice(task: Task): void {
    try {
        const toastId = timerCapNoticeId(task.id);
        if (toast.isActive(toastId)) return;
        toast.warning(({closeToast}) => <TimerCapNotice task={task} closeToast={closeToast}/>, {
            toastId,
            autoClose: false,
            closeOnClick: false,
            className: 'u-message-warning',
        });
    }
    catch (err) {
        console.error('OWA-525: could not show the timer notice', err);
    }
}
